import { unwrapApiResponse } from '../../../api/responseNormalizer';
import type {
  PtpCall,
  SimilarMatchKind,
  SimilarVessel,
  VesselLeg,
  VesselLink,
  VesselLinkedBy,
  VesselMasterItem,
  VesselMasterList,
  VesselMasterSource,
  VesselSuggestion,
} from '../types/vesselMaster';

/*
 * Readers for the Java answers. They keep the Java field names and only guard
 * against missing or mistyped values, so a screen never meets `undefined`.
 */

type JsonRecord = Record<string, unknown>;

const asRecord = (value: unknown): JsonRecord =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};

const asList = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

export const readText = (value: unknown): string | null =>
  value === null || value === undefined || value === '' ? null : String(value);

export const readId = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
};

const readLeg = (value: unknown): VesselLeg => (value === 'OFF' ? 'OFF' : 'LOADING');

export const readPtpCall = (raw: unknown): PtpCall => {
  const row = asRecord(raw);
  return {
    id: readId(row.id) ?? 0,
    port: String(row.port ?? ''),
    vesselName: String(row.vesselName ?? ''),
    vesselCode: readText(row.vesselCode),
    lineId: readText(row.lineId),
    voyage: readText(row.voyage),
    scn: readText(row.scn),
    arriveAt: readText(row.arriveAt),
    arriveEstimated: row.arriveEstimated === true,
    departAt: readText(row.departAt),
    departEstimated: row.departEstimated === true,
    dryCutoff: readText(row.dryCutoff),
    reeferCutoff: readText(row.reeferCutoff),
    cfsCutoff: readText(row.cfsCutoff),
    hazardCutoff: readText(row.hazardCutoff),
    listed: row.listed !== false,
    lastSeenAt: readText(row.lastSeenAt),
    portLabel: readText(row.portLabel),
    voyageOut: readText(row.voyageOut),
    status: readText(row.status),
    omitted: row.omitted === true,
    yardOpening: readText(row.yardOpening),
    berth: readText(row.berth),
    actualArriveAt: readText(row.actualArriveAt),
    actualDepartAt: readText(row.actualDepartAt),
    terminal: readText(row.terminal),
    callSign: readText(row.callSign),
    lastPort: readText(row.lastPort),
    shippingAgent: readText(row.shippingAgent),
  };
};

export const readPtpCalls = (raw: unknown): PtpCall[] => asList(raw).map(readPtpCall);

export const readSuggestions = (body: unknown): VesselSuggestion[] =>
  asList(unwrapApiResponse<unknown>(body)).map((raw) => {
    const row = asRecord(raw);
    return {
      vesselMasterId: readId(row.vesselMasterId),
      vesselName: String(row.vesselName ?? ''),
      vesselCode: readText(row.vesselCode),
      lineId: readText(row.lineId),
      matchedAlias: readText(row.matchedAlias),
      calls: readPtpCalls(row.calls),
    };
  });

const MATCH_KINDS: SimilarMatchKind[] = ['SAME', 'CLOSE', 'NONE'];

export const readSimilar = (body: unknown): SimilarVessel => {
  const row = asRecord(unwrapApiResponse<unknown>(body));
  const match = MATCH_KINDS.find((kind) => kind === row.match) ?? 'NONE';
  return {
    match,
    vesselMasterId: match === 'NONE' ? null : readId(row.vesselMasterId),
    vesselName: match === 'NONE' ? null : readText(row.vesselName),
  };
};

const LINKED_BY: VesselLinkedBy[] = ['PICKED', 'TYPED', 'MATCHED'];

export const readLinks = (body: unknown): VesselLink[] =>
  asList(unwrapApiResponse<unknown>(body)).map((raw) => {
    const row = asRecord(raw);
    return {
      leg: readLeg(row.leg),
      vesselMasterId: readId(row.vesselMasterId),
      vesselName: String(row.vesselName ?? ''),
      ptpVesselCallId: readId(row.ptpVesselCallId),
      linkedBy: LINKED_BY.find((kind) => kind === row.linkedBy) ?? 'TYPED',
      call: row.call ? readPtpCall(row.call) : null,
    };
  });

const SOURCES: VesselMasterSource[] = ['PTP', 'TYPED', 'HISTORY', 'MANUAL'];

export const readMasterItem = (raw: unknown): VesselMasterItem => {
  const row = asRecord(raw);
  return {
    id: readId(row.id) ?? 0,
    vesselName: String(row.vesselName ?? ''),
    vesselCode: readText(row.vesselCode),
    lineId: readText(row.lineId),
    source: SOURCES.find((source) => source === row.source) ?? 'TYPED',
    aliases: asList(row.aliases).map(String),
    jobCount: Number(row.jobCount) || 0,
  };
};

export const readMasterList = (body: unknown): VesselMasterList => {
  const row = asRecord(unwrapApiResponse<unknown>(body));
  const items = asList(row.items).map(readMasterItem);
  return { total: Number(row.total) || items.length, items };
};

export const readMasterItemBody = (body: unknown): VesselMasterItem =>
  readMasterItem(unwrapApiResponse<unknown>(body));
